/**
 * ReporteTrabajosEspeciales.jsx
 * Reporte ejecutivo de Trabajos Especiales con filtros y exportación a Excel.
 *
 * Octavio 29-ago-2026.
 */

import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../auth/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEP_COLORS = {
  FONTANERIA: { color: "#0F766E", bg: "#F0FDFA", label: "Fontanería",       icon: "fa-faucet-drip" },
  HERRERIA:   { color: "#B45309", bg: "#FFFBEB", label: "Herrería y Pintura", icon: "fa-screwdriver-wrench" },
};

// ── Tile de resumen ───────────────────────────────────────────────────────────
function Tile({ label, value, icon, color }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5 flex items-center gap-4">
      <div className="w-12 h-12 rounded-xl flex items-center justify-center text-white text-xl flex-shrink-0"
           style={{ background: color }}>
        <i className={`fa-solid ${icon}`} />
      </div>
      <div>
        <p className="text-2xl font-bold text-slate-900">{value ?? "—"}</p>
        <p className="text-xs text-slate-500">{label}</p>
      </div>
    </div>
  );
}

export default function ReporteTrabajosEspeciales() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const hoy = new Date().toISOString().slice(0, 10);
  const primerDiaMes = hoy.slice(0, 8) + "01";

  const [filtros, setFiltros] = useState({
    departamento: "",
    fecha_ini:    primerDiaMes,
    fecha_fin:    hoy,
    no_empleado:  "",
    unidad:       "",
  });
  const [resumen, setResumen]   = useState(null);
  const [datos, setDatos]       = useState([]);
  const [total, setTotal]       = useState(0);
  const [loading, setLoading]   = useState(false);
  const [exportando, setExportando] = useState(false);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(
        Object.entries(filtros).filter(([, v]) => v !== "")
      );
      const [resR, datR] = await Promise.all([
        axios.get(`${API}/admin/trabajos-especiales/resumen`, { params, withCredentials: true }),
        axios.get(`${API}/admin/trabajos-especiales`, { params: { ...params, limit: 200 }, withCredentials: true }),
      ]);
      setResumen(resR.data);
      setDatos(datR.data.datos || []);
      setTotal(datR.data.total || 0);
    } catch (err) {
      toast.error("Error al cargar el reporte");
    } finally {
      setLoading(false);
    }
  }, [filtros]);

  useEffect(() => { cargar(); }, [cargar]);

  const exportar = async () => {
    setExportando(true);
    try {
      const params = new URLSearchParams(
        Object.fromEntries(Object.entries(filtros).filter(([, v]) => v !== ""))
      );
      const resp = await axios.get(
        `${API}/admin/trabajos-especiales/exportar-excel?${params}`,
        { withCredentials: true, responseType: "blob" }
      );
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `reporte_trabajos_especiales_${hoy}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Excel descargado ✓");
    } catch {
      toast.error("Error al exportar");
    } finally {
      setExportando(false);
    }
  };

  const setF = (k, v) => setFiltros(f => ({ ...f, [k]: v }));
  const inputCls = "border border-slate-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white";

  // Calcular totales del resumen
  const tot = resumen?.por_departamento?.reduce(
    (acc, d) => ({
      act:     acc.act + (d.total_act || 0),
      pin:     acc.pin + (d.pintura_l || 0),
      sol:     acc.sol + (d.soldadura_kg || 0),
      thi:     acc.thi + (d.thinner_l || 0),
      gas:     acc.gas + (d.gasolina_l || 0),
      fotos:   acc.fotos + (d.total_fotos || 0),
    }),
    { act: 0, pin: 0, sol: 0, thi: 0, gas: 0, fotos: 0 }
  );

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-6">
      <div className="max-w-6xl mx-auto">

        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button onClick={() => navigate("/hub")} className="text-slate-400 hover:text-slate-600 text-xl">
            <i className="fa-solid fa-arrow-left" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Reporte · Trabajos Especiales</h1>
            <p className="text-sm text-slate-500">Fontanería · Herrería y Pintura · DMU 2026</p>
          </div>
          <div className="ml-auto flex gap-2">
            <button
              onClick={cargar}
              disabled={loading}
              className="px-4 py-2 rounded-xl text-sm font-medium border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              <i className="fa-solid fa-rotate-right mr-2" />Actualizar
            </button>
            <button
              onClick={exportar}
              disabled={exportando}
              className="px-4 py-2 rounded-xl text-sm font-medium bg-green-700 text-white hover:bg-green-800 disabled:opacity-50"
            >
              {exportando
                ? <><i className="fa-solid fa-spinner fa-spin mr-2" />Exportando…</>
                : <><i className="fa-solid fa-file-excel mr-2" />Exportar Excel</>}
            </button>
          </div>
        </div>

        {/* Filtros */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5 mb-6">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Filtros</p>
          <div className="flex flex-wrap gap-3">
            <select className={inputCls} value={filtros.departamento} onChange={e => setF("departamento", e.target.value)}>
              <option value="">Todos los departamentos</option>
              <option value="FONTANERIA">Fontanería</option>
              <option value="HERRERIA">Herrería y Pintura</option>
            </select>
            <div className="flex items-center gap-2">
              <label className="text-xs text-slate-500">Del</label>
              <input type="date" className={inputCls} value={filtros.fecha_ini} onChange={e => setF("fecha_ini", e.target.value)} />
              <label className="text-xs text-slate-500">al</label>
              <input type="date" className={inputCls} value={filtros.fecha_fin} onChange={e => setF("fecha_fin", e.target.value)} />
            </div>
            <input type="text" placeholder="No. empleado" className={inputCls}
              value={filtros.no_empleado} onChange={e => setF("no_empleado", e.target.value)} />
            <input type="text" placeholder="Unidad" className={inputCls}
              value={filtros.unidad} onChange={e => setF("unidad", e.target.value)} />
            <button
              onClick={() => setFiltros({ departamento: "", fecha_ini: primerDiaMes, fecha_fin: hoy, no_empleado: "", unidad: "" })}
              className="px-3 py-1.5 text-xs text-slate-500 hover:text-slate-700 border border-slate-200 rounded-lg bg-white"
            >
              Limpiar
            </button>
          </div>
        </div>

        {/* Tiles de resumen */}
        {tot && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
            <Tile label="Actividades totales" value={tot.act} icon="fa-list-check"    color="#1E3A5F" />
            <Tile label="Pintura (L)"          value={tot.pin.toFixed(1)} icon="fa-fill-drip"  color="#0F766E" />
            <Tile label="Soldadura (kg)"        value={tot.sol.toFixed(1)} icon="fa-fire"       color="#B45309" />
            <Tile label="Thinner (L)"           value={tot.thi.toFixed(1)} icon="fa-droplet"    color="#7C3AED" />
            <Tile label="Gasolina (L)"          value={tot.gas.toFixed(1)} icon="fa-gas-pump"   color="#DC2626" />
            <Tile label="Fotos"                 value={tot.fotos}          icon="fa-images"     color="#0369A1" />
          </div>
        )}

        {/* Resumen por departamento */}
        {resumen?.por_departamento?.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            {resumen.por_departamento.map(d => {
              const c = DEP_COLORS[d._id] || {};
              return (
                <div key={d._id} className="rounded-2xl shadow-sm border p-5"
                     style={{ background: c.bg || "#fff", borderColor: c.color + "33" }}>
                  <div className="flex items-center gap-3 mb-3">
                    <i className={`fa-solid ${c.icon || "fa-circle"} text-lg`} style={{ color: c.color }} />
                    <h3 className="font-bold text-slate-900">{c.label || d._id}</h3>
                    <span className="ml-auto text-2xl font-bold" style={{ color: c.color }}>{d.total_act}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-xs text-slate-600">
                    <div><span className="font-medium">Pintura:</span> {(d.pintura_l || 0).toFixed(1)} L</div>
                    <div><span className="font-medium">Soldadura:</span> {(d.soldadura_kg || 0).toFixed(1)} kg</div>
                    <div><span className="font-medium">Thinner:</span> {(d.thinner_l || 0).toFixed(1)} L</div>
                    <div><span className="font-medium">Gasolina:</span> {(d.gasolina_l || 0).toFixed(1)} L</div>
                    <div><span className="font-medium">Fotos:</span> {d.total_fotos || 0}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Tabla de detalle */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <h3 className="font-semibold text-slate-800 text-sm">Detalle de actividades</h3>
            <span className="text-xs text-slate-500">{datos.length} de {total} registro(s)</span>
          </div>

          {loading ? (
            <div className="text-center py-12 text-slate-400">
              <i className="fa-solid fa-spinner fa-spin mr-2" />Cargando…
            </div>
          ) : datos.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              Sin registros para los filtros seleccionados.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50">
                  <tr>
                    {["Dep.","Fecha","Empleado","Unidad","Actividad","Ubicación","P(L)","S(kg)","T(L)","G(L)","Fotos","Origen"].map(h => (
                      <th key={h} className="px-3 py-2 text-left font-semibold text-slate-600 whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {datos.map((d, i) => {
                    const c = DEP_COLORS[d.departamento] || {};
                    return (
                      <tr key={d.id || i} className="border-t border-slate-50 hover:bg-slate-50/60">
                        <td className="px-3 py-2">
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold text-white"
                                style={{ background: c.color || "#64748B" }}>
                            {d.departamento?.slice(0, 4)}
                          </span>
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">{d.fecha || "—"}</td>
                        <td className="px-3 py-2">
                          <div>{d.no_empleado || "—"}</div>
                          {d.nombre && <div className="text-slate-400">{d.nombre}</div>}
                        </td>
                        <td className="px-3 py-2">{d.unidad || "—"}</td>
                        <td className="px-3 py-2 max-w-[180px] truncate">{d.actividad}</td>
                        <td className="px-3 py-2 max-w-[120px] truncate">{d.ubicacion || "—"}</td>
                        <td className="px-3 py-2 text-right">{d.pintura_l ?? "—"}</td>
                        <td className="px-3 py-2 text-right">{d.soldadura_kg ?? "—"}</td>
                        <td className="px-3 py-2 text-right">{d.thinner_l ?? "—"}</td>
                        <td className="px-3 py-2 text-right">{d.gasolina_l ?? "—"}</td>
                        <td className="px-3 py-2 text-center">
                          {(d.fotos?.length || 0) > 0
                            ? <span className="px-2 py-0.5 rounded-full bg-teal-50 text-teal-700 font-medium">
                                <i className="fa-solid fa-image text-[10px] mr-1" />{d.fotos.length}
                              </span>
                            : <span className="text-slate-300">—</span>}
                        </td>
                        <td className="px-3 py-2 text-slate-400">{d.origen === "excel_importacion" ? "Excel" : "Web"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
